/**
 * Sticky Add-to-Cart — Variant-Bridge
 *
 * Die Produktseiten nutzen NICHT den Dawn-Standard-Picker (<variant-selects> /
 * <variant-radios>), sondern den Tiered-Quantity-Selector (".tqs-bundle-selector"
 * mit ".tqs-card" / ".tqs-variant-row" / "select.tqs-variant-select").
 * Die alte Version hat ausschliesslich nach <variant-selects> gesucht, nichts
 * gefunden und die Auswahl im Sticky-Bar deshalb ins Leere laufen lassen —
 * dadurch landete immer die zuletzt im TQS aktive Variante im Warenkorb.
 *
 * Diese Version bedient in dieser Reihenfolge:
 *   1. TQS  (aktive Karte)
 *   2. Dawn <variant-selects> / <variant-radios>   (Fallback)
 *   3. Direkter Patch des versteckten input[name="id"]  (Sicherheitsnetz)
 *
 * Zusaetzlich: hat die aktive TQS-Karte mehrere Positionen (2er-/3er-Bundle),
 * wird der Sticky-Selector ausgeblendet. Ein einzelnes Dropdown kann einen
 * Mehrfach-Bundle-Zustand nicht korrekt abbilden — lieber kein Selector als
 * ein Selector, der die Auswahl des Kunden still ueberschreibt.
 *
 * Ergovo (05.08.2026) — Variantenbild im Sticky-Bar:
 * Das Thumbnail wurde bisher nur EINMAL serverseitig gerendert und danach nie
 * aktualisiert. Wechselte der Kunde die Farbe, blieb das Bild der Erst-Variante
 * stehen. Jetzt wird bei jeder Variantenaenderung das Bild der AKTUELL
 * gewaehlten Variante gezeigt.
 *
 * Quelle der Wahrheit ist bewusst das versteckte input[name="id"] der
 * Warenkorb-Form (das patchen ALLE Picker: TQS, Dawn, Sticky selbst) —
 * nicht der Sticky-Select. Dadurch stimmt das Bild auch dann, wenn der Kunde
 * oben im TQS umschaltet statt im Sticky-Bar.
 *
 * Hat eine Variante kein eigenes Bild (z. B. reine Groessen-Option), bleibt das
 * urspruengliche Produktbild stehen — kein leerer Platzhalter.
 */
class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.mainScope = document.querySelector('#MainContent') || document;

    this.productForm =
      this.mainScope.querySelector('.product-form__buttons') ||
      this.mainScope.querySelector('product-form') ||
      document.querySelector('.product-form__buttons') ||
      document.querySelector('product-form');

    if (!this.productForm) return;

    this.button = this.querySelector('[data-sticky-atc-trigger]');
    this.optionsWrap = this.querySelector('[data-sticky-options]');
    this.imageEl = this.querySelector('.lumin-sticky-cart__image');
    this._forwarding = false;

    // Ausgangsbild merken: Fallback fuer Varianten ohne eigenes Bild.
    if (this.imageEl) {
      this._defaultImageSrc = this.imageEl.getAttribute('src') || '';
      this._defaultImageAlt = this.imageEl.getAttribute('alt') || '';
    }

    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }

    this.setupOptionForwarding();
    this.setupSyncObservers();

    // Initialzustand: Sticky-Auswahl an den echten Picker angleichen.
    this.syncFromMainPicker();
    this.updateOptionsVisibility();
    this.updateStickyImage();

    // Sticky-Cart NUR anzeigen, wenn der echte Button bereits NACH OBEN
    // aus dem Viewport gescrollt ist (User ist daran vorbei).
    const updateVisibility = (entry) => {
      const passedAbove = entry.boundingClientRect.bottom < 0;
      if (passedAbove) {
        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');
      } else {
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
      }
    };

    this.io = new IntersectionObserver(([entry]) => updateVisibility(entry), {
      threshold: [0, 1],
    });
    this.io.observe(this.productForm);

    this._onScroll = () => {
      const rect = this.productForm.getBoundingClientRect();
      updateVisibility({ boundingClientRect: rect });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._priceObserver) this._priceObserver.disconnect();
    if (this._buttonObserver) this._buttonObserver.disconnect();
    if (this._buttonTextObserver) this._buttonTextObserver.disconnect();
    if (this._variantIdObserver) this._variantIdObserver.disconnect();
  }

  handleClick(event) {
    event.preventDefault();

    // Vor dem Submit die Sticky-Auswahl garantiert in den echten Picker
    // schreiben — auch wenn der User nichts geaendert hat (Race-Schutz).
    this.forwardToMainPicker();

    const realButton =
      this.mainScope.querySelector('.product-form__submit') ||
      this.mainScope.querySelector('product-form button[type="submit"]') ||
      document.querySelector('.product-form__submit');

    if (realButton && !realButton.disabled) realButton.click();
  }

  /* ─── Picker-Auffindung ────────────────────────────────────── */

  getTqsWrap() {
    return (
      this.mainScope.querySelector('.tqs-bundle-selector') ||
      document.querySelector('.tqs-bundle-selector')
    );
  }

  getTqsActiveCard() {
    const wrap = this.getTqsWrap();
    if (!wrap) return null;
    return wrap.querySelector('.tqs-card--active') || wrap.querySelector('.tqs-card');
  }

  getDawnPicker() {
    return this.mainScope.querySelector('variant-selects, variant-radios');
  }

  getStickySelects() {
    if (!this.optionsWrap) return [];
    return Array.from(this.optionsWrap.querySelectorAll('select[data-option-index]')).sort(
      (a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10)
    );
  }

  /* Zeilen der aktiven TQS-Karte. Karten ohne .tqs-variant-row (z. B. Produkte
     ohne Optionen) liefern die Karte selbst als einzige "Zeile" zurueck. */
  getTqsRows(card) {
    if (!card) return [];
    const rows = Array.from(card.querySelectorAll('.tqs-variant-row'));
    if (rows.length) return rows;
    return card.querySelector('.tqs-variant-select') ? [card] : [];
  }

  /* ─── Forwarding: Sticky  →  echter Picker ───────────────────────── */

  setupOptionForwarding() {
    if (!this.optionsWrap) return;

    this.getStickySelects().forEach((select) => {
      select.addEventListener('change', () => {
        if (this._forwarding) return;
        this.forwardToMainPicker();
      });
    });
  }

  forwardToMainPicker() {
    const stickySelects = this.getStickySelects();
    if (!stickySelects.length) return;

    const card = this.getTqsActiveCard();

    if (card) {
      const rows = this.getTqsRows(card);

      // Mehrfach-Bundle: nicht anfassen, der Kunde hat pro Position gewaehlt.
      if (rows.length > 1) {
        this.updateOptionsVisibility();
        return;
      }

      const row = rows[0];
      if (row) {
        stickySelects.forEach((stickySelect) => {
          const position = parseInt(stickySelect.dataset.optionIndex, 10) + 1;
          const target = row.querySelector(
            '.tqs-variant-select[data-option-position="' + position + '"]'
          );
          if (target && target.value !== stickySelect.value) {
            target.value = stickySelect.value;
          }
        });

        // TQS hoert auf 'change' der einzelnen Selects und patcht daraufhin
        // input[name="id"] — deshalb dort feuern, nicht am Container.
        row.querySelectorAll('.tqs-variant-select').forEach((sel) => {
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // Sicherheitsnetz, falls TQS-Handler (noch) nicht gebunden ist.
      this.patchHiddenVariantId();
      return;
    }

    const mainPicker = this.getDawnPicker();
    if (mainPicker) {
      if (mainPicker.tagName.toLowerCase() === 'variant-selects') {
        const mainSelects = Array.from(mainPicker.querySelectorAll('select'));
        if (mainSelects.length) {
          stickySelects.forEach((stickySelect) => {
            const index = parseInt(stickySelect.dataset.optionIndex, 10);
            const mainSelect = mainSelects[index];
            if (mainSelect) mainSelect.value = stickySelect.value;
          });
        } else {
          // Dawn 15 rendert innerhalb von <variant-selects> Radio-Fieldsets.
          this.applyToFieldsets(mainPicker, stickySelects);
        }
      } else {
        this.applyToFieldsets(mainPicker, stickySelects);
      }

      mainPicker.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Weder TQS noch Dawn-Picker: direkt die Form patchen.
    this.patchHiddenVariantId();
  }

  applyToFieldsets(mainPicker, stickySelects) {
    const fieldsets = Array.from(mainPicker.querySelectorAll('fieldset'));
    stickySelects.forEach((stickySelect) => {
      const index = parseInt(stickySelect.dataset.optionIndex, 10);
      const fieldset = fieldsets[index];
      if (!fieldset) return;
      const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === stickySelect.value
      );
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ─── Sicherheitsnetz: input[name="id"] direkt setzen ─────────── */

  getVariantsData() {
    if (this._variants !== undefined) return this._variants;
    const el = this.querySelector('[data-sticky-variants]');
    if (!el) {
      this._variants = null;
      return null;
    }
    try {
      this._variants = JSON.parse(el.textContent);
    } catch (e) {
      this._variants = null;
    }
    return this._variants;
  }

  resolveVariantFromSticky() {
    const variants = this.getVariantsData();
    if (!variants || !variants.length) return null;
    const values = this.getStickySelects().map((s) => s.value);
    if (!values.length) return null;
    return (
      variants.find((v) =>
        values.every((val, i) => String(v['option' + (i + 1)]) === String(val))
      ) || null
    );
  }

  getCartForm() {
    return (
      this.mainScope.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  getVariantIdInput() {
    const form = this.getCartForm();
    if (!form) return null;
    return (
      form.querySelector('input[name="id"].product-variant-id') ||
      form.querySelector('input[name="id"]')
    );
  }

  patchHiddenVariantId() {
    const variant = this.resolveVariantFromSticky();
    if (!variant) return;

    const input = this.getVariantIdInput();
    if (input && input.value !== String(variant.id)) {
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.button) this.button.disabled = variant.available === false;
  }

  /* ─── Bild der aktuell gewaehlten Variante ─────────────────────── */

  /* id → Bild-URL. Nur Varianten MIT eigenem Bild landen in der Map; alles
     andere faellt bewusst auf das Ausgangsbild zurueck. */
  getVariantImageMap() {
    if (this._imageMap !== undefined) return this._imageMap;

    const variants = this.getVariantsData();
    this._imageMap = null;

    if (variants && variants.length) {
      const map = {};
      variants.forEach((v) => {
        const img = v && v.featured_image;
        const src = img && (img.src || img.url);
        if (src) map[String(v.id)] = { src: src, alt: img.alt || '' };
      });
      this._imageMap = map;
    }

    return this._imageMap;
  }

  /* Aus dem Varianten-JSON kommt die Original-Datei (oft > 1 MB). Fuer ein
     48x48-Thumbnail wird die CDN-Breite begrenzt — sonst kostet jeder
     Farbwechsel unnoetig Ladezeit. */
  thumbUrl(src) {
    if (!src || /[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }

  /* Quelle der Wahrheit: input[name="id"]. Faellt darauf zurueck, die Variante
     aus den Sticky-Selects aufzuloesen, falls die Form (noch) nicht da ist. */
  getActiveVariantId() {
    const input = this.getVariantIdInput();
    if (input && input.value) return String(input.value);
    const variant = this.resolveVariantFromSticky();
    return variant ? String(variant.id) : null;
  }

  updateStickyImage() {
    if (!this.imageEl) return;

    const map = this.getVariantImageMap();
    if (!map) return;

    const id = this.getActiveVariantId();
    const entry = id ? map[id] : null;

    const nextSrc = this.thumbUrl(entry ? entry.src : this._defaultImageSrc);
    if (!nextSrc) return;

    if (this.imageEl.getAttribute('src') !== nextSrc) {
      this.imageEl.setAttribute('src', nextSrc);
      this.imageEl.setAttribute(
        'alt',
        (entry && entry.alt) || this._defaultImageAlt || ''
      );
    }
  }

  /* ─── Sync: echter Picker  →  Sticky ─────────────────────────── */

  setupSyncObservers() {
    const tqsWrap = this.getTqsWrap();
    if (tqsWrap) {
      tqsWrap.addEventListener('change', () => {
        this.syncFromMainPicker();
        this.updateOptionsVisibility();
        // Ein Tick warten: TQS patcht input[name="id"] in seinem eigenen
        // change-Handler; sonst lesen wir noch die alte Variante.
        window.setTimeout(() => this.updateStickyImage(), 0);
      });
      tqsWrap.addEventListener('click', () => {
        // Kartenwechsel laeuft ueber click; TQS setzt danach die Klassen.
        window.setTimeout(() => {
          this.syncFromMainPicker();
          this.updateOptionsVisibility();
          this.updateStickyImage();
        }, 0);
      });
    }

    const mainPicker = this.getDawnPicker();
    if (mainPicker) {
      mainPicker.addEventListener('change', () => {
        this.syncFromMainPicker();
        window.setTimeout(() => this.updateStickyImage(), 0);
      });
    }

    // Sicherheitsnetz fuer jeden Picker, den wir hier nicht namentlich kennen:
    // sobald sich input[name="id"] aendert, zieht das Bild nach.
    const cartForm = this.getCartForm();
    if (cartForm) {
      cartForm.addEventListener('change', () => this.updateStickyImage());
    }
    const variantInput = this.getVariantIdInput();
    if (variantInput) {
      this._variantIdObserver = new MutationObserver(() => this.updateStickyImage());
      this._variantIdObserver.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    const priceEl = this.mainScope.querySelector('[id^="price-"]');
    const stickyPriceEl = this.querySelector('.lumin-sticky-cart__price');
    if (priceEl && stickyPriceEl) {
      this._priceObserver = new MutationObserver(() => {
        stickyPriceEl.innerHTML = priceEl.innerHTML;
        // Preis aendert sich nur bei Variantenwechsel — verlaesslichster
        // Trigger, wenn ein Picker weder Event noch Attribut hinterlaesst.
        this.updateStickyImage();
      });
      this._priceObserver.observe(priceEl, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const submitButton = this.mainScope.querySelector('.product-form__submit');
    if (submitButton && this.button) {
      this._buttonObserver = new MutationObserver(() => this.syncButtonState(submitButton));
      this._buttonObserver.observe(submitButton, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
      const textSpan = submitButton.querySelector('span');
      if (textSpan) {
        this._buttonTextObserver = new MutationObserver(() => this.syncButtonState(submitButton));
        this._buttonTextObserver.observe(textSpan, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }
  }

  syncButtonState(submitButton) {
    if (!this.button) return;
    this.button.disabled = submitButton.disabled;
    const sourceSpan = submitButton.querySelector('span');
    const targetSpan = this.button.querySelector('span');
    if (sourceSpan && targetSpan) targetSpan.textContent = sourceSpan.textContent.trim();
  }

  syncFromMainPicker() {
    const stickySelects = this.getStickySelects();
    if (!stickySelects.length) return;

    this._forwarding = true;

    const card = this.getTqsActiveCard();
    if (card) {
      const row = this.getTqsRows(card)[0];
      if (row) {
        stickySelects.forEach((stickySelect) => {
          const position = parseInt(stickySelect.dataset.optionIndex, 10) + 1;
          const source = row.querySelector(
            '.tqs-variant-select[data-option-position="' + position + '"]'
          );
          if (source && stickySelect.value !== source.value) {
            stickySelect.value = source.value;
          }
        });
      }
      this._forwarding = false;
      return;
    }

    const mainPicker = this.getDawnPicker();
    if (mainPicker) {
      const mainSelects = Array.from(mainPicker.querySelectorAll('select'));
      if (mainSelects.length) {
        stickySelects.forEach((stickySelect) => {
          const index = parseInt(stickySelect.dataset.optionIndex, 10);
          const mainSelect = mainSelects[index];
          if (mainSelect && stickySelect.value !== mainSelect.value) {
            stickySelect.value = mainSelect.value;
          }
        });
      } else {
        const fieldsets = Array.from(mainPicker.querySelectorAll('fieldset'));
        stickySelects.forEach((stickySelect) => {
          const index = parseInt(stickySelect.dataset.optionIndex, 10);
          const fieldset = fieldsets[index];
          if (!fieldset) return;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked && stickySelect.value !== checked.value) {
            stickySelect.value = checked.value;
          }
        });
      }
    }

    this._forwarding = false;
  }

  /* Selector verstecken, sobald ein Mehrfach-Bundle aktiv ist. */
  updateOptionsVisibility() {
    if (!this.optionsWrap) return;
    const card = this.getTqsActiveCard();
    const multi = card ? this.getTqsRows(card).length > 1 : false;
    this.optionsWrap.hidden = multi;
    this.classList.toggle('lumin-sticky-cart--no-options', multi);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
